import { useState, useEffect } from 'react';
import PlainTextExpandingInput from './PTS/PlainTextExpandingInput';

const DEFAULT_SOURCES = [
  'Isännöitsijätodistus',
  'Kohdekäynti',
  'Huoneistorekisteri',
  'Kiinteistörekisteri',
];

// Hook to detect if the screen is small (mobile)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}

export default function EditableRow({
  otsikko = null,
  item,
  field,
  idField,
  updateFn,
  editable = true,
  showSource = true,
  options = null,
  fontSize = undefined,
}) {
  const valueFromData = item?.[field] ?? '';
  const sourceFromData = item?.metadata?.[field]?.source ?? '';

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(valueFromData);
  const [source, setSource] = useState(sourceFromData);
  const [tempAlert, setTempAlert] = useState(null);
  const isMobile = useIsMobile();

  const startEdit = () => setIsEditing(true);
  const cancelEdit = () => {
    setValue(valueFromData);
    setSource(sourceFromData);
    setIsEditing(false);
  };

  const saveEdit = async () => {
    setIsEditing(false);
    const updates = [];
    const hasValueChanged = value !== valueFromData;
    const hasSourceChanged = source !== sourceFromData;

    if (hasValueChanged) {
      const obj = { [field]: value };
      if (hasSourceChanged) obj.metadata = { [field]: { source } };
      updates.push(obj);
    }
    if (!hasValueChanged && hasSourceChanged) {
      updates.push({ [field]: valueFromData, metadata: { [field]: { source } } });
    }
    if (updates.length === 0) return;

    try {
      await updateFn(item[idField], updates);
      setTempAlert({ type: 'success', message: `"${otsikko ?? field}" päivitetty onnistuneesti!` });
      setTimeout(() => setTempAlert(null), 4000);
    } catch (err) {
      setTempAlert({
        type: 'danger',
        message: `Kentän "${otsikko ?? field}" tallennus epäonnistui: ${err.response?.data?.message || err.message}`,
      });
      setTimeout(() => setTempAlert(null), 4000);
    }
  };

  const displayValue = (() => {
    if (!options) return value;
    const code = Object.entries(options).find(([_, label]) => label === value)?.[0];
    return options[code] || value || 'Ei tiedossa';
  })();

  return (
    <>
      {otsikko !== null && <dt className="col-sm-3">{otsikko}</dt>}

      {tempAlert && (
        <div className={`alert alert-${tempAlert.type} p-2`} role="alert">
          {tempAlert.message}
        </div>
      )}

      <dd className="col-sm-9" style={{ whiteSpace: 'normal' }}>
        {isEditing ? (
          <div className="d-flex flex-column gap-2">
            {options ? (
              <select
                className="form-select form-select-sm"
                value={Object.entries(options).find(([_, label]) => label === value)?.[0] || ''}
                onChange={(e) => setValue(options[e.target.value] || '')}
                autoFocus
              >
                <option value="">-- Valitse --</option>
                {Object.entries(options).map(([code, label]) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            ) : (
              <PlainTextExpandingInput
                namespace={`editable-row-${idField}-${field}`}
                value={value}
                onChange={setValue}
              />
            )}

            {showSource && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  placeholder="Lähde"
                  list={`sources-${idField}-${field}`}
                  style={{ minHeight: 31, padding: '3px 5px 4px', fontWeight: 400, color: '#495057' }}
                />
                <datalist id={`sources-${idField}-${field}`}>
                  {DEFAULT_SOURCES.map((src) => (
                    <option key={src} value={src} />
                  ))}
                </datalist>
              </div>
            )}

            <div className="d-flex gap-2 mt-1">
              <button
                className={isMobile ? 'btn btn-primary' : 'btn btn-primary btn-sm'}
                onClick={saveEdit}
                type="button"
                style={isMobile ? { minWidth: '70px', fontSize: '0.95rem' } : {}}
              >
                Save
              </button>
              <button
                className={isMobile ? 'btn btn-secondary' : 'btn btn-secondary btn-sm'}
                onClick={cancelEdit}
                type="button"
                style={isMobile ? { minWidth: '70px', fontSize: '0.95rem' } : {}}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (() => {
            const isUnknown = !displayValue || displayValue === 'Ei tiedossa';
            // No pencil icon — the whole value+source area is the click
            // target, with a hover tint as the only affordance that it's
            // editable. Scoped <style> repeated per-instance is the same
            // pattern already used elsewhere in this app for per-row
            // hover CSS (e.g. BuildingListRail.jsx) — harmless duplication,
            // avoids a separate global stylesheet entry for one rule.
            return (
              <>
                {editable && (
                  <style>{`
                    .editable-row-hover { transition: background-color 0.12s ease; }
                    .editable-row-hover:hover { background-color: #eafbf1; }
                    .editable-row-hover span:first-of-type { display: inline-block; transition: font-size 0.12s ease; }
                    .editable-row-hover:hover span:first-of-type { font-size: 1.1em; }
                  `}</style>
                )}
                <div
                  className={editable ? 'editable-row-hover' : undefined}
                  onClick={editable ? startEdit : undefined}
                  role={editable ? 'button' : undefined}
                  tabIndex={editable ? 0 : undefined}
                  style={{ cursor: editable ? 'pointer' : 'default', borderRadius: 6, padding: '2px 4px', margin: '-2px -4px' }}
                >
                  <span style={{ ...(fontSize ? { fontSize } : {}), ...(isUnknown ? { color: '#dc3545', fontWeight: 600 } : {}) }}>
                    {isUnknown && <i className="bi bi-exclamation-circle me-1" style={{ fontSize: '0.85em' }} />}
                    {displayValue || 'Ei tiedossa'}
                  </span>
                  {showSource && <small className="text-muted d-block" style={fontSize ? { fontSize } : undefined}>Lähde: {source || 'Ei tiedossa'}</small>}
                </div>
              </>
            );
          })()}
      </dd>
    </>
  );
}
